/**
 * Response Templates Modal
 * Allows staff to select and insert pre-defined response templates
 */
import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { ScrollArea } from '../ui/scroll-area';
import { Search, FileText, X, ChevronRight } from 'lucide-react';
import { DEFAULT_TEMPLATES, TEMPLATE_CATEGORIES } from './constants';

export const ResponseTemplatesModal = ({ 
  isOpen, 
  onClose, 
  onSelect,
  customTemplates = [] 
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  
  if (!isOpen) return null;
  
  // Merge default and custom templates
  const allTemplates = [...DEFAULT_TEMPLATES, ...customTemplates];
  
  // Filter templates
  const filteredTemplates = allTemplates.filter(template => {
    const matchesSearch = template.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          template.content.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || template.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });
  
  const handleSelect = (template) => {
    onSelect(template.content);
    onClose();
  };
  
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="p-4 border-b flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-emerald-600" />
            <h2 className="text-lg font-semibold">Modèles de réponse</h2>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>
        
        {/* Search and Filters */}
        <div className="p-4 border-b space-y-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Rechercher un modèle..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
          
          <div className="flex gap-2 flex-wrap">
            <Badge
              variant={selectedCategory === 'all' ? 'default' : 'outline'}
              className={`cursor-pointer ${selectedCategory === 'all' ? 'bg-emerald-600' : ''}`}
              onClick={() => setSelectedCategory('all')}
            >
              Tous
            </Badge>
            {Object.entries(TEMPLATE_CATEGORIES).map(([key, cat]) => (
              <Badge
                key={key}
                variant={selectedCategory === key ? 'default' : 'outline'}
                className={`cursor-pointer ${selectedCategory === key ? 'bg-emerald-600' : ''}`}
                onClick={() => setSelectedCategory(key)}
              >
                {cat.icon} {cat.label}
              </Badge>
            ))}
          </div>
        </div>
        
        {/* Templates List */}
        <ScrollArea className="flex-1 p-4">
          {filteredTemplates.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <FileText className="h-12 w-12 mx-auto mb-2 opacity-50" />
              <p>Aucun modèle trouvé</p>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredTemplates.map((template) => (
                <div
                  key={template.id}
                  onClick={() => handleSelect(template)}
                  className="p-3 border rounded-lg hover:border-emerald-300 hover:bg-emerald-50 cursor-pointer transition-colors group"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium text-gray-800">{template.name}</span>
                        <Badge variant="outline" className="text-xs">
                          {TEMPLATE_CATEGORIES[template.category]?.icon} {TEMPLATE_CATEGORIES[template.category]?.label}
                        </Badge>
                      </div>
                      <p className="text-sm text-gray-500 line-clamp-2">
                        {template.content}
                      </p>
                    </div>
                    <ChevronRight className="h-5 w-5 text-gray-400 group-hover:text-emerald-600 flex-shrink-0 ml-2" />
                  </div>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
        
        {/* Footer */}
        <div className="p-4 border-t bg-gray-50 text-xs text-gray-500">
          {filteredTemplates.length} modèle(s) disponible(s) • Cliquez pour insérer
        </div>
      </div>
    </div>
  );
};

export default ResponseTemplatesModal;
